export interface SelfOnboardingDetailsInput {
  canSelfOnboardTeam: boolean;
  hasTeam: boolean;
  hasJoinableTeams: boolean;
  isUserSpecialtyEmpty: boolean;
  isUserFirstNameEmpty: boolean;
  isUserLastNameEmpty: boolean;
}

/**
 * Whether a user who can self-onboard a team still owes details: a team to
 * join, a specialty, or a name. `can_self_onboard_team` stays `true` after
 * they join, so the flag alone does not say onboarding is done.
 */
export const computeNeedsSelfOnboardingDetails = ({
  canSelfOnboardTeam,
  hasTeam,
  hasJoinableTeams,
  isUserSpecialtyEmpty,
  isUserFirstNameEmpty,
  isUserLastNameEmpty,
}: SelfOnboardingDetailsInput): boolean =>
  canSelfOnboardTeam &&
  // `hasTeam` only sees the current workspace, so also require a team left to join.
  ((!hasTeam && hasJoinableTeams) ||
    isUserSpecialtyEmpty ||
    isUserFirstNameEmpty ||
    isUserLastNameEmpty);

/** The slice of the user record onboarding eligibility reads. */
export interface OnboardingEligibilityUser {
  onboarding_pending_at?: string | null;
  can_self_onboard_team?: boolean | null;
  specialty_id?: string | null;
  other_specialty?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

export interface OnboardingEligibilityInput {
  /** `undefined` or `null` while the user has not loaded. */
  user: OnboardingEligibilityUser | null | undefined;
  /** Same-tab override: the in-app steps finished before the user refetch cleared the mark. */
  hasCompletedOnboarding: boolean;
  hasTeam: boolean;
  isJoinedTeamsPending: boolean;
  joinableTeams: readonly unknown[] | undefined;
  isJoinableTeamsPending: boolean;
}

export interface OnboardingEligibility {
  /** The user has cleared every onboarding step that would otherwise redirect or interrupt them. */
  isOnboarded: boolean;
  /** The inputs are still loading; `isOnboarded` is `false` until they settle. */
  isPending: boolean;
}

const isBlank = (value: string | null | undefined): boolean => !value;

/**
 * Whether the user has finished onboarding, as the app shell and the
 * self-onboarding modal decide it: an `onboarding_pending_at` user is sent to
 * the in-app flow, and a user who can self-onboard a team but still owes a
 * team, name or specialty gets the self-onboarding modal wherever they land.
 */
export function computeOnboardingEligibility({
  user,
  hasCompletedOnboarding,
  hasTeam,
  isJoinedTeamsPending,
  joinableTeams,
  isJoinableTeamsPending,
}: OnboardingEligibilityInput): OnboardingEligibility {
  const canSelfOnboardTeam = !!user?.can_self_onboard_team;
  // The team reads only matter for a self-onboarding user, and the joinable
  // teams query is disabled (so pending forever) for everyone else.
  const isTeamsPending =
    canSelfOnboardTeam && (isJoinedTeamsPending || isJoinableTeamsPending);
  const isPending = !user || isTeamsPending;

  const needsSelfOnboardingDetails = computeNeedsSelfOnboardingDetails({
    canSelfOnboardTeam,
    hasTeam,
    hasJoinableTeams: (joinableTeams?.length ?? 0) > 0,
    isUserSpecialtyEmpty: isBlank(user?.specialty_id) && isBlank(user?.other_specialty),
    isUserFirstNameEmpty: isBlank(user?.first_name),
    isUserLastNameEmpty: isBlank(user?.last_name),
  });

  const isOnboarded =
    !isPending &&
    !!user &&
    (!user.onboarding_pending_at || hasCompletedOnboarding) &&
    !needsSelfOnboardingDetails;

  return { isOnboarded, isPending };
}
